import { useQuery } from "@tanstack/react-query";
import { productsServices } from "../services/productsServices.js";
import { departmentsServices } from "../services/departmentsServices.js";
import { useState, useMemo, useEffect } from "react";
import {
  LoadingSpinner,
  ErrorMessage,
  NotFoundMessage,
  PaginationControls,
} from "../components/auxiliarComponents/auxiliarComponents.jsx";
import CardProduct from "../components/CardProduct/CardProduct.jsx";
import { useParams } from "react-router-dom";
import { useDepartmentsCache } from "../hooks/useDapartmentsCache.js";
import ProductFilter from "../components/FilterProduct/FilterProduct";

const ITEMS_PER_PAGE = 10;

export default function DepartmentProdutos() {
  const { departamento } = useParams();
  const [page, setPage] = useState(1);
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Departments query
  const {
    data: departments,
    isLoading: isLoadingDepartments,
    isError: isDepartmentsError,
    error: departmentsError,
  } = useQuery({
    queryKey: ["departments"],
    queryFn: departmentsServices.getDepartments,
    staleTime: 1000 * 60 * 10, // 10 minutes cache
  });

  // Store departments in cache
  useDepartmentsCache(departments);

  // Memoized department data with safe normalization
  const { departmentSelecionada, departmentId } = useMemo(() => {
    if (!departments || !departamento)
      return { departmentSelecionada: null, departmentId: null };

    try {
      // Normalize the URL parameter
      const formattedDepartment = departamento
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/-/g, " ")
        .toLowerCase();

      const found = departments.find((dept) => {
        // Get the formatted name from cache or create it
        const deptName = dept.name
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();

        return deptName === formattedDepartment;
      });

      return {
        departmentSelecionada: found || null,
        departmentId: found?.id || null,
      };
    } catch (error) {
      console.error("Error finding department:", error);
      return { departmentSelecionada: null, departmentId: null };
    }
  }, [departments, departamento]);

  // Products query
  const {
    data: productsResponse,
    isLoading: isLoadingProducts,
    isError: isProductsError,
    error: productsError,
    isFetching,
    isPreviousData,
  } = useQuery({
    enabled: !!departmentId,
    queryKey: ["produtos-department", departmentId, page],
    queryFn: () =>
      productsServices.getProductsByDepartament(departmentId, {
        page,
        limit: ITEMS_PER_PAGE,
      }),
    keepPreviousData: true,
    retry: 1,
  });

  // Memoized products data
  const products = useMemo(() => {
    if (!productsResponse) return [];
    if (Array.isArray(productsResponse)) return productsResponse;
    if (productsResponse?.data) return productsResponse.data;
    return [];
  }, [productsResponse]);

  // Update filtered products when products change
  useEffect(() => {
    setFilteredProducts(products);
  }, [products]);

  // Total pages
  const totalPages = useMemo(() => {
    if (productsResponse?.pagination?.totalPages) {
      return productsResponse.pagination.totalPages;
    }
    if (productsResponse?.totalPages) {
      return productsResponse.totalPages;
    }
    if (productsResponse?.total) {
      return Math.ceil(productsResponse.total / ITEMS_PER_PAGE);
    }
    return 0;
  }, [productsResponse]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (isLoadingDepartments) {
    return <LoadingSpinner message="Carregando departamentos..." />;
  }

  if (isDepartmentsError) {
    return <ErrorMessage message="Erro ao carregar departamentos" />;
  }

  if (!departmentId) {
    return <NotFoundMessage message="Departamento não encontrado" />;
  }

  if (isLoadingProducts && !isPreviousData) {
    return (
      <div className="container mx-auto p-4">
        <h2 className="text-2xl font-bold mb-4">
          Produtos do Departamento: {departmentSelecionada?.name}
        </h2>
        <LoadingSpinner message="Carregando produtos..." />
      </div>
    );
  }

  if (isProductsError) {
    return (
      <div className="container mx-auto p-4">
        <h2 className="text-2xl font-bold mb-4">
          Produtos do Departamento: {departmentSelecionada?.name}
        </h2>
        <ErrorMessage message="Erro ao carregar produtos" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-2 mmb:mt-48 smb:mt-44 xmb:mt-44 Iphones:mt-44 Iphones2:mt-44 lg:mt-32 xl:mt-32">
      <h2 className="text-2xl md:text-3xl font-bold uppercase mb-4 m-[1%]">
        Produtos do Departamento:{" "}
        <span className="text-green-600">"{departmentSelecionada?.name}"</span>
      </h2>

      <div className="flex flex-col md:flex-row gap-4 px-1 md:px-0 md:gap-4 xl:px-4 xl:gap-4">
        {/* Sidebar de filtros */}
        <div className="w-full md:w-1/6 h-fit sticky top-20 z-10 self-start">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <ProductFilter
              products={products}
              updateFilteredProducts={setFilteredProducts}
            />
          </div>
        </div>

        {/* Área dos cards */}
        <div className="w-full md:w-4/5">
          {filteredProducts.length === 0 && !isFetching ? (
            <NotFoundMessage message="Nenhum produto encontrado com esses filtros" />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 justify-items-center">
                {filteredProducts.map((product) => (
                  <CardProduct
                    key={`${product.id}-${page}`}
                    product={product}
                  />
                ))}
              </div>
              {isFetching && (
                <LoadingSpinner small message="Carregando mais produtos..." />
              )}
            </>
          )}
        </div>
      </div>
      <div className="flex justify-center my-6 gap-4">
        <button
          disabled={page === 1}
          onClick={() => handlePageChange(page - 1)}
          className="bg-gray-200 px-4 py-2 rounded disabled:opacity-50"
        >
          Anterior
        </button>

        <span className="px-4 py-2">
          {page} / {totalPages}
        </span>

        <button
          disabled={page === totalPages}
          onClick={() => handlePageChange(page + 1)}
          className="bg-gray-200 px-4 py-2 rounded disabled:opacity-50"
        >
          Próxima
        </button>
      </div>
    </div>
  );
}
